import * as React from "react";
import { observer } from "mobx-react";
import { PulseBetMarketComponentData } from "../../data/models";
import { InstantCollapsible, Dropdown } from "sb-resp-ui";

// imported only for typings
// these are passed to the component from index.ts
import { BaseDict } from "./dict";
import { PulseBetMarketsData, BetPlacementModel, BetPlacementStatus } from "./data";
import { MessageModifiers } from "../../data";

import * as SCMP from "../../components";
import * as CMP from "./components";

@observer
export class PulseBetMarkets extends React.Component<{
    dataProvider: PulseBetMarketsData
    dict: BaseDict
}, {
    dataProvider: PulseBetMarketsData
}> {
    dict: BaseDict
    countDownValues: string[]
    constructor(props) {
        super(props);

        // setup dictionary
        this.dict = this.props.dict;
        this.countDownValues = ["3", "2", "1", this.dict.GoLabel];
        // setup data provider
        this.state = {
            dataProvider: this.props.dataProvider
        }
    }

    // A check needed to show which component should be rendered
    renderPulseBetContainer(pulseBetMarket: PulseBetMarketComponentData) {
        return (
            <div className="rj-pulse-bet-market__collapsible-wrap" key={`${pulseBetMarket.pulseBetMetadata.name}`}>
                <InstantCollapsible
                    trigger={
                        <SCMP.Heading label={pulseBetMarket.pulseBetMetadata.name} isDesktop={this.state.dataProvider.deviceDetect.isDesktop} tooltipMessage={this.dict[`Branch${pulseBetMarket.pulseBetMetadata.branchID}_${pulseBetMarket.pulseBetMetadata.typeId}_`]} />
                    }
                    content={
                        <div className="rj-pulse-bet-market-main-container" >
                            {this.state.dataProvider.eventsBetPlacementResult.map(data =>
                                pulseBetMarket.pulseBetMetadata.marketId === data.eventId &&
                                this.renderMarket(data, pulseBetMarket))}
                        </div>
                    }
                    initiallyExpanded={true}
                />

            </div>
        )
    }

    renderPulseContent(pulseBetMarket: PulseBetMarketComponentData, betPlacementData: BetPlacementModel) {
        let isDisabled = this.state.dataProvider.isDanger || pulseBetMarket.pulseBetMetadata.isDanger;

        return (
            <div className="rj-pulse-bet-markets" data-suspended={isDisabled}>

                <SCMP.Winnings inputData={pulseBetMarket.winings} onStakeChangeFading={pulseBetMarket.onStakeChangeFading} />

                <CMP.TimePeriod periodFrames={pulseBetMarket.periodFrames.map(period => `${period} ${this.state.dataProvider.addSuffix(pulseBetMarket.pulseBetMetadata.marketId)}`)} />

                <div className="rj-pulse-bet-markets__pre-bet">
                    <SCMP.SlimProgressbar eventID={pulseBetMarket.pulseBetMetadata.marketId} />
                </div>

                <div className="rj-pulse-bet-markets__btns-holder">

                    <div className="rj-my-pulse-bets__btn-outer" hidden={!pulseBetMarket.freeBetsEnabled}>
                        <Dropdown data={pulseBetMarket.freeBetOptions} selectItem={pulseBetMarket.onFreePulseBetSelect} blockName="pulse-bet" defaultText={this.dict.SelectFreeBet} />
                    </div>
                    <div className="rj-my-pulse-bets__btn-outer" hidden={pulseBetMarket.freeBetsEnabled}>
                        <CMP.StakeHolder data={this.state.dataProvider.stakeInputDataView(pulseBetMarket.pulseBetMetadata.marketId)} />
                    </div>

                    <div className="rj-my-pulse-bets__pulse-btn-outer">
                        {this.renderPulseBetButton(pulseBetMarket, isDisabled)}
                    </div>
                </div>

                <CMP.FreeBet isActive={pulseBetMarket.hasFreeBetOptions} text={this.state.dataProvider.stakeTypeText(pulseBetMarket.pulseBetMetadata.typeId)} toggleStakeType={pulseBetMarket.toggleStakeType} />

                <div className="rj-pulse-bet-markets__messages">
                    {betPlacementData.status === BetPlacementStatus.Error && <CMP.ErrorMessage error={betPlacementData.error} />}
                    {this.state.dataProvider.isUserLogged &&
                        this.state.dataProvider.userInfo.TaxProvider && !!this.state.dataProvider.userInfo.TaxProvider.TaxInfo.CountryTax &&
                        <CMP.GeneralMessage text={this.dict.GermanTaxesMessage.format(this.state.dataProvider.userInfo.TaxProvider.TaxInfo.CountryTax)} modifier={MessageModifiers.Tax} />}
                </div>
            </div>
        )
    }

    renderPulseBetButton(pulseBetMarket: PulseBetMarketComponentData, isDisabled: boolean) {
        return (
            <SCMP.PulseBetButton handler={pulseBetMarket.externalPlaceBetHandler}
                isDisabled={isDisabled || !this.state.dataProvider.isUserGeolocated || this.state.dataProvider.isPlacingBetsDisabled || (this.state.dataProvider.IsSeamlessOrWallet() && !this.state.dataProvider.isUserLogged)}>
                {this.state.dataProvider.placeButtonText(pulseBetMarket.pulseBetMetadata.typeId)}
            </SCMP.PulseBetButton>
        )
    }

    getInfoMessages() {
       return this.state.dataProvider.store.infoMessages.map(x => <CMP.GeneralMessage text={x} />)
    }

    renderAcceptedBetMessage(betPlacementData: BetPlacementModel) {
        return (
            <div className="rj-pulse-bet-markets__success">
                <CMP.AcceptedBet data={betPlacementData.success} />
                {this.getInfoMessages()}
            </div>
        )
    }

    renderProcessBetMessage() {
        return (
            <div className="rj-pulse-bet-markets__in-process">
                <CMP.PulseLoading />
                {this.getInfoMessages()}
            </div>
        )
    }

    renderMarket(betPlacementData: BetPlacementModel, pulseBetMarket: PulseBetMarketComponentData) {
        return (
            <React.Fragment key={`${pulseBetMarket.pulseBetMetadata.marketId}`}>
                {this.renderPulseBetMarketView(pulseBetMarket, betPlacementData)}
            </React.Fragment>
        )
    }

    renderPulseBetMarketView(pulseBetMarket: PulseBetMarketComponentData, betPlacementData: BetPlacementModel) {
        switch (betPlacementData.status) {
            case BetPlacementStatus.InProcess:
                return this.renderProcessBetMessage();

            case BetPlacementStatus.Success:
                return this.renderAcceptedBetMessage(betPlacementData);

            case BetPlacementStatus.Error:
            case BetPlacementStatus.NotStarted:
            default:
                return this.renderPulseContent(pulseBetMarket, betPlacementData);
        }
    }

    render() {
        return (
            <>
                {this.state.dataProvider.store.pulsebetsDataForComponents.map((data) => this.renderPulseBetContainer(data))}
            </ >
        )
    }
}
